import type { CanvasSectionDef } from '@ispot/shared'
import { DOMAIN_HEX } from '@/lib/constants/domainColors'

export type CanvasSectionTheme = 'market' | 'business' | 'solution'

const LEGACY_COLOR_THEME_MAP: Record<string, CanvasSectionTheme> = {
  '#00b894': 'market',
  '#5c8a6b': 'market',
  '#0984e3': 'business',
  '#f9c148': 'solution',
  '#2bbcc7': 'market',
  '#3196e2': 'business',
  '#ffc05f': 'solution',
}

const NAMESPACE_THEME_MAP: Array<{ prefix: string; theme: CanvasSectionTheme }> = [
  { prefix: 'http://www.intellispot.com/ontology/market#', theme: 'market' },
  { prefix: 'http://www.intellispot.com/ontology/business#', theme: 'business' },
  { prefix: 'http://www.intellispot.com/ontology/solution#', theme: 'solution' },
]

const SECTION_THEME_STYLES = {
  market: {
    color: DOMAIN_HEX.market.base,
    bgColor: 'white',
    tintColor: 'var(--color-market-100)',
    textClassName: 'text-market-700',
    borderClassName: 'border-market/25',
    softBgClassName: 'bg-market/10',
    softBorderClassName: 'border-market/30',
    badgeVariant: 'market' as const,
  },
  business: {
    color: DOMAIN_HEX.business.base,
    bgColor: 'white',
    tintColor: 'var(--color-business-100)',
    textClassName: 'text-business-700',
    borderClassName: 'border-business/25',
    softBgClassName: 'bg-business/10',
    softBorderClassName: 'border-business/30',
    badgeVariant: 'business' as const,
  },
  solution: {
    color: DOMAIN_HEX.solution.base,
    bgColor: 'white',
    tintColor: 'var(--color-solution-100)',
    textClassName: 'text-solution-700',
    borderClassName: 'border-solution/25',
    softBgClassName: 'bg-solution/10',
    softBorderClassName: 'border-solution/30',
    badgeVariant: 'solution' as const,
  },
} as const

export function inferCanvasSectionTheme(def: Pick<CanvasSectionDef, 'color' | 'classUris'>): CanvasSectionTheme {
  const colorTheme = LEGACY_COLOR_THEME_MAP[def.color.toLowerCase()]
  if (colorTheme) {
    return colorTheme
  }

  const uriTheme = def.classUris
    .map((uri) => NAMESPACE_THEME_MAP.find((candidate) => uri.startsWith(candidate.prefix))?.theme)
    .find(Boolean)

  return uriTheme ?? 'business'
}

export function getCanvasSectionThemeStyles(theme: CanvasSectionTheme) {
  return SECTION_THEME_STYLES[theme]
}

export function alpha(hex: string, opacity: number): string {
  const normalized = hex.replace('#', '')
  if (normalized.length !== 6) {
    return hex
  }

  const red = Number.parseInt(normalized.slice(0, 2), 16)
  const green = Number.parseInt(normalized.slice(2, 4), 16)
  const blue = Number.parseInt(normalized.slice(4, 6), 16)

  return `rgba(${red}, ${green}, ${blue}, ${opacity})`
}
